import React, { useMemo, useState } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CubeIcon, ShoppingBagIcon, CurrencyRupeeIcon, ArrowUturnLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useQuery } from '../../hooks';
import { productsAPI, ordersAPI, returnsAPI } from '../../services/api';
import { StatCard, LoadingPage, Card, Badge, StatusBadge, EmptyState, Button } from '../../components/common/UI';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/authSlice';
import { format, subDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { OrderDetailModal } from './OrdersFinance';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-surface-100 bg-white p-3 shadow-card-lg text-xs">
      <p className="font-semibold text-surface-600 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name === 'revenue' ? `₹${Number(p.value).toLocaleString('en-IN')}` : p.value}
        </p>
      ))}
    </div>
  );
};

export default function BusinessDashboard({ business }) {
  const user = useSelector(selectUser);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const { data: allOrdersData, loading: allOrdLoading } = useQuery(
    () => ordersAPI.myOrders({ limit: 200, business_id: business?.id }),
    [business?.id],
    { enabled: !!business?.id }
  );

  const { data: recentOrdersData, loading: recentOrdLoading } = useQuery(
    () => ordersAPI.myOrders({ limit: 5, business_id: business?.id }),
    [business?.id],
    { enabled: !!business?.id }
  );

  const { data: productsData, loading: prodLoading } = useQuery(
    () => productsAPI.list({ limit: 5, business_id: business?.id }),
    [business?.id],
    { enabled: !!business?.id }
  );

  const { data: allProductsData } = useQuery(
    () => productsAPI.list({ limit: 1, business_id: business?.id }),
    [business?.id],
    { enabled: !!business?.id }
  );

  const { data: returnsData } = useQuery(
    () => returnsAPI.adminList({ limit: 100, business_id: business?.id }),
    [business?.id],
    { enabled: !!business?.id }
  );

  const allOrders = allOrdersData?.rows || [];
  const recentOrders = recentOrdersData?.rows || [];
  const recentProducts = productsData?.rows || productsData?.data?.rows || [];
  const allReturns = returnsData?.rows || (Array.isArray(returnsData) ? returnsData : []);

  const totalOrdersCount = allOrdersData?.count ?? allOrders.length;
  const totalProductCount = allProductsData?.count ?? recentProducts.length;

  const totalRevenue = useMemo(() =>
    allOrders
      .filter(o => !['cancelled', 'refunded'].includes(o.status))
      .reduce((sum, o) => {
        const businessItemsTotal = (o.items || [])
          .filter(item => String(item.business_id) === String(business?.id))
          .reduce((itemSum, item) => itemSum + Number(item.total_price || 0), 0);
        return sum + businessItemsTotal;
      }, 0),
    [allOrders, business?.id]
  );

  const pendingReturns = useMemo(() =>
    allReturns.filter(r => r.status === 'requested').length,
    [allReturns]
  );

  const chartData = useMemo(() =>
    Array.from({ length: 14 }, (_, i) => {
      const date = subDays(new Date(), 13 - i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayOrders = allOrders.filter(o => {
        const d = o.created_at || o.createdAt;
        return d && format(new Date(d), 'yyyy-MM-dd') === dateStr;
      });
      const revenue = dayOrders
        .filter(o => !['cancelled', 'refunded'].includes(o.status))
        .reduce((sum, o) => {
          const businessItemsTotal = (o.items || [])
            .filter(item => String(item.business_id) === String(business?.id))
            .reduce((itemSum, item) => itemSum + Number(item.total_price || 0), 0);
          return sum + businessItemsTotal;
        }, 0);
      return { date: format(date, 'dd MMM'), revenue, orders: dayOrders.length };
    }),
    [allOrders, business?.id]
  );

  return (
    <div className="animate-in space-y-6">

      {/* Welcome */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Welcome back, {user?.first_name}! 👋</h1>
          <p className="mt-0.5 text-sm text-surface-500">{format(new Date(), 'EEEE, dd MMMM yyyy')}</p>
        </div>
        {business?.status !== 'approved' && (
          <div className="flex items-center gap-2 rounded-xl border border-warning-200 bg-warning-50 px-4 py-2.5 text-sm text-warning-700">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
            Business status: <Badge variant="yellow">{business?.status?.replace(/_/g, ' ')}</Badge>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total Revenue"
          value={allOrdLoading ? '...' : `₹${totalRevenue.toLocaleString('en-IN')}`}
          icon={CurrencyRupeeIcon}
          color="green"
        />
        <StatCard
          label="Total Orders"
          value={allOrdLoading ? '...' : totalOrdersCount}
          icon={ShoppingBagIcon}
          color="blue"
        />
        <StatCard
          label="Products"
          value={prodLoading ? '...' : totalProductCount}
          icon={CubeIcon}
          color="purple"
        />
        <StatCard
          label="Pending Returns"
          value={pendingReturns}
          icon={ArrowUturnLeftIcon}
          color="red"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display font-semibold text-surface-900">Revenue (14 days)</h2>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="bizRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="revenue" name="revenue" stroke="#22c55e" strokeWidth={2} fill="url(#bizRevGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display font-semibold text-surface-900">Orders (14 days)</h2>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="orders" fill="#6366f1" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display font-semibold text-surface-900">Recent Orders</h2>
            <Link to="/business/orders"><Button variant="ghost" size="sm">View all →</Button></Link>
          </div>
          {recentOrdLoading ? <LoadingPage /> : recentOrders.length === 0 ? (
            <EmptyState icon={ShoppingBagIcon} title="No orders yet" />
          ) : (
            <div className="space-y-2">
              {recentOrders.map(o => (
                <div
                  key={o.id}
                  className="flex items-center justify-between rounded-xl bg-surface-50 px-3 py-2.5 cursor-pointer hover:bg-surface-100 transition-colors"
                  onClick={() => setSelectedOrderId(o.id)}
                  title="Click to view order details"
                >
                  <div>
                    <p className="text-sm font-semibold font-mono text-brand-700">{o.order_number}</p>
                    <p className="text-xs text-surface-400">{o.items?.length} item{o.items?.length !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm font-semibold">₹{Number(o.total_amount).toLocaleString('en-IN')}</p>
                    <StatusBadge status={o.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display font-semibold text-surface-900">Recent Products</h2>
            <Link to="/business/products"><Button variant="ghost" size="sm">View all →</Button></Link>
          </div>
          {prodLoading ? <LoadingPage /> : recentProducts.length === 0 ? (
            <EmptyState icon={CubeIcon} title="No products yet" description="Add your first product to start selling" />
          ) : (
            <div className="space-y-2">
              {recentProducts.map(p => (
                <div key={p.id} className="flex items-center gap-3 rounded-xl bg-surface-50 px-3 py-2.5">
                  {p.images?.[0]?.url
                    ? <img src={p.images[0].url} alt={p.name} className="h-9 w-9 rounded-lg object-cover flex-shrink-0" />
                    : <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-surface-200"><CubeIcon className="h-4 w-4 text-surface-400" /></div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-surface-900 truncate">{p.name}</p>
                    <p className="text-xs text-surface-400">Stock: {p.inventory?.stock_qty ?? '—'}</p>
                  </div>
                  <span className="font-mono text-sm font-semibold">₹{Number(p.sale_price || p.base_price).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

      </div>

      {/* Order Detail Modal */}
      <OrderDetailModal
        orderId={selectedOrderId}
        open={!!selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        onUpdated={() => { }}
      />
    </div>
  );
}
